import { attr, hasMany, type AsyncHasMany } from '@ember-data/model';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core-types/symbols';
import type StoreService from 'scaleflow/services/store';
import nameSort from 'scaleflow/utils/nameSort';
import type GoalModel from './goal';
import type InitiativeModel from './initiative';
import OwnerModel from './owner';
import type TeamModel from './team';

export default class DepartmentModel extends OwnerModel {
  declare [Type]: 'department';
  @service declare store: StoreService;

  @attr('string') declare name: string;

  @hasMany('team', { async: true, inverse: 'department' })
  declare teams: AsyncHasMany<TeamModel>;

  @attr declare text_delta: unknown;
  get initiatives() {
    const allInitiatives = (this.teams.content ?? []).reduce<InitiativeModel[]>(
      (acc, team) => {
        acc.push(...(team.initiatives.content ?? []));
        return acc;
      },
      [],
    );
    return [...new Set(allInitiatives)];
  }

  get goalsFromInitiatives() {
    const initiatives = this.initiatives;
    // console.log('initiatives', initiatives);
    const goalsSet = new Set<GoalModel>();
    initiatives.forEach((initiative) => {
      (initiative.goals.content ?? []).forEach((goal) => {
        goalsSet.add(goal);
      });
    });
    return Array.from(goalsSet);
  }

  get activeTeams() {
    return (this.teams.content ?? []).filter((team) => team.active);
  }

  get teamIds(): (string | null)[] {
    return (this as DepartmentModel).hasMany('teams').ids();
  }

  get sortedGoals() {
    const goals = this.goals.content ?? [];
    return [...goals].sort((a, b) => nameSort(a.title, b.title));
  }

  get combinedGoalsFromTeamsInitiativesAndDepartment() {
    const goalsFromInitiatives = this.goalsFromInitiatives.map((g) => g.id);
    const departmentLinkedGoals = (this.goals.content ?? []).map((g) => g.id);
    const uniqueGoalIds = Array.from(
      new Set([...goalsFromInitiatives, ...departmentLinkedGoals]),
    );
    const uniqueGoals = uniqueGoalIds
      .map((id) => this.store.peekRecord<GoalModel>('goal', id as string))
      .filter((goal) => !!goal);
    return uniqueGoals;
  }

  get nowNextInitiatives() {
    const initiatives = this.initiatives;
    const nowNextInitiatives = initiatives.filter((initiative) =>
      ['ACTIVE', 'READY FOR PLANNING', 'READY'].includes(initiative.status),
    );
    return nowNextInitiatives;
  }
}
